// Copyright 2025, University of Maryland, All Rights Reserved
import { CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { RefreshCw } from "lucide-react"
import { SessionDisplay } from "@/components/SessionDisplay"
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import type { APISession } from "@/types/session"
import { getAllAPISessions } from "@/types/session"
import type { WorkspaceBillImpactScore } from "@/types/workspace-bill"

interface WorkspaceBillsPageHeaderProps {
  debounced_min_impact_score: WorkspaceBillImpactScore
  selected_session: APISession
  on_session_change: (session: string) => void
  on_refresh: () => void
  is_loading: boolean
}

export function WorkspaceBillsPageHeader({
  debounced_min_impact_score,
  selected_session,
  on_session_change,
  on_refresh,
  is_loading,
}: WorkspaceBillsPageHeaderProps) {
  const { currentWorkspace } = useAuth()

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
      <div>
        <CardTitle>Legislative Bills</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Bills filtered by impact score {debounced_min_impact_score} and above
          {currentWorkspace && (
            <span className="ml-2">• Workspace: {currentWorkspace.name || currentWorkspace.wsId}</span>
          )}
        </p>
      </div>
      <div className="flex gap-3 items-center w-full sm:w-auto">
        <Select
          value={selected_session || undefined}
          onValueChange={on_session_change}
        >
          <SelectTrigger className="w-[200px] sm:w-[200px]">
            <SelectValue placeholder="Select session">
              {selected_session && <SessionDisplay session={selected_session} />}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {getAllAPISessions().map((session) => (
              <SelectItem key={session} value={session}>
                <SessionDisplay session={session} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button
          variant="outline"
          size="sm"
          onClick={on_refresh}
          disabled={is_loading}
          className="flex items-center gap-2 h-9 px-3"
        >
          <RefreshCw className={`h-4 w-4 ${is_loading ? 'animate-spin' : ''}`} />
          Reload
        </Button>
      </div>
    </div>
  )
}
